/**
 * @description
 * Anthropic assignment service for managing user assignments to workspaces.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { BaseAPIClient } from "../core/base-client";

/**
 * Anthropic assignment service for managing user assignments
 */
export class AnthropicAssignmentService extends BaseAPIClient {
  /**
   * Bulk assign workspace keys to users
   */
  async assignWorkspaceUsers(workspaceId: string, data: {
    userIds: string[];
    clientId: string;
  }): Promise<{
    success: boolean;
    message: string;
    assignedUsers: number;
    workspace: {
      id: string;
      name: string;
      anthropicId: string;
    };
    apiKey: {
      id: string;
      name: string;
      preview: string;
    };
  }> {
    return this.request(`/anthropic/workspaces/${workspaceId}/assign-users`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Assign Anthropic key to specific user
   */
  async assignUserAnthropicKey(clientId: string, userId: string, workspaceId: string): Promise<{
    success: boolean;
    message: string;
    user: {
      id: string;
      email: string;
      name: string;
      anthropicWorkspaceId: string | null;
      anthropicApiKeyPreview: string | null;
    };
    workspace: {
      id: string;
      name: string;
      anthropicId: string;
    };
    apiKey: {
      id: string;
      name: string;
      preview: string;
    };
  }> {
    return this.request(`/anthropic/clients/${clientId}/users/${userId}/anthropic-key`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId }),
    });
  }

  /**
   * Remove Anthropic key assignment from user
   */
  async removeUserAnthropicKey(clientId: string, userId: string): Promise<{
    success: boolean;
    message: string;
    user: any;
  }> {
    return this.request(`/anthropic/clients/${clientId}/users/${userId}/anthropic-key`, {
      method: 'DELETE',
    });
  }

  /**
   * Get all Anthropic key assignments for client
   */
  async getClientAnthropicAssignments(clientId: string): Promise<{
    success: boolean;
    assignments: Array<{
      user: {
        id: string;
        email: string;
        name: string;
        role: string;
      };
      workspace: {
        id: string;
        name: string;
        anthropicId: string;
      };
      apiKey: {
        preview: string;
        assignedAt: string;
      };
    }>;
    total: number;
  }> {
    return this.request(`/anthropic/clients/${clientId}/anthropic-assignments`);
  }
}

// Export singleton instance
export const anthropicAssignmentService = new AnthropicAssignmentService();
